import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Loader2, Clapperboard, Link2, UploadCloud, Plus, X } from "lucide-react";
import { fetchAdminReels, moderateReel, deleteReel, fetchReelBlob,
         addReelLink, adminReelUploadStart, adminReelUploadChunk, adminReelUploadFinish, formatApiError } from "@/lib/api";

const CHUNK = 8 * 1024 * 1024;

function ReelPreview({ token, reel }) {
    const [src, setSrc] = useState(null);
    const isEmbed = reel.provider === "youtube" || reel.provider === "instagram";
    const isLinkFile = reel.provider === "file";
    useEffect(() => {
        if (isEmbed || isLinkFile) return undefined;
        let alive = true;
        let url = null;
        fetchReelBlob(token, reel.id).then((u) => { url = u; if (alive) setSrc(u); }).catch(() => {});
        return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [token, reel.id, isEmbed, isLinkFile]);
    if (isEmbed) {
        return <iframe src={reel.embed_url} title={reel.trip || "reel"} loading="lazy"
            className="w-full aspect-[9/16] rounded-xl bg-black" allow="autoplay; encrypted-media; picture-in-picture"
            data-testid={`admin-reel-embed-${reel.id}`} />;
    }
    if (isLinkFile) {
        return <video src={reel.embed_url} controls playsInline preload="metadata"
            className="w-full aspect-[9/16] object-cover rounded-xl bg-black" data-testid={`admin-reel-video-${reel.id}`} />;
    }
    if (!src) return <div className="w-full aspect-[9/16] bg-stone-800 rounded-xl flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-stone-500" /></div>;
    return <video src={src} controls playsInline preload="metadata" className="w-full aspect-[9/16] object-cover rounded-xl bg-black" data-testid={`admin-reel-video-${reel.id}`} />;
}

function detectProvider(url) {
    if (/youtube\.com\/(watch\?[^#\s]*v=|shorts\/)|youtu\.be\//.test(url)) return "YouTube embed";
    if (/instagram\.com\/(reels?|p)\//.test(url)) return "Instagram embed";
    if (/\.(mp4|mov|webm)([?#]|$)/i.test(url)) return "Direct video link";
    return null;
}

function AddReelPanel({ token, onDone }) {
    const [mode, setMode] = useState("link");
    const [f, setF] = useState({ url: "", trip: "", name: "" });
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const provider = f.url.trim() ? detectProvider(f.url.trim()) : null;

    const submitLink = async () => {
        if (!provider) return toast.error("Paste a YouTube, Instagram, or direct .mp4/.mov/.webm link (https)");
        setBusy(true);
        try {
            await addReelLink(token, { url: f.url.trim(), trip: f.trip.trim(), person_name: f.name.trim() || "GhumneChaley Team" });
            toast.success("Reel added — it's live on the homepage");
            onDone();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not add the link"); }
        finally { setBusy(false); }
    };

    const submitFile = async () => {
        if (!file) return toast.error("Pick a video file first");
        if (file.size > 150 * 1024 * 1024) return toast.error("Video must be under 150MB");
        setBusy(true);
        try {
            const { upload_id, max_chunk } = await adminReelUploadStart(token, {
                name: f.name.trim(), trip: f.trip.trim(), filename: file.name, size: file.size });
            const step = Math.min(CHUNK, max_chunk);
            for (let off = 0; off < file.size; off += step) {
                await adminReelUploadChunk(token, upload_id, file.slice(off, off + step));
                setProgress(Math.round((Math.min(file.size, off + step) / file.size) * 100));
            }
            await adminReelUploadFinish(token, upload_id);
            toast.success("Reel uploaded — it's live on the homepage");
            onDone();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed — please try again"); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-4 rounded-2xl border border-saffron/50 bg-cream/60 p-4" data-testid="add-reel-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex rounded-full border border-stone-300 overflow-hidden text-[11px] font-black uppercase tracking-wider">
                    {[["link", "Paste a link", Link2], ["upload", "Upload video", UploadCloud]].map(([k, l, Icon]) => (
                        <button key={k} onClick={() => setMode(k)} data-testid={`add-reel-mode-${k}`}
                            className={`px-4 py-2 inline-flex items-center gap-1.5 ${mode === k ? "bg-navy text-white" : "bg-white text-stone-500 hover:text-navy"}`}>
                            <Icon className="h-3.5 w-3.5" /> {l}
                        </button>
                    ))}
                </div>
                <button onClick={onDone} data-testid="add-reel-cancel" className="text-stone-400 hover:text-navy"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {mode === "link" && (
                    <div className="sm:col-span-2">
                        <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="add-reel-url"
                            placeholder="https://youtube.com/shorts/… or instagram.com/reel/… or https://…/video.mp4"
                            className="w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-saffron" />
                        {f.url.trim() && (
                            <p className={`mt-1.5 text-[11px] font-bold ${provider ? "text-green-700" : "text-red-500"}`} data-testid="add-reel-provider-hint">
                                {provider ? `Detected: ${provider} — will play inside the homepage reel frame` : "Unsupported link — use YouTube, Instagram, or a direct video file URL"}
                            </p>
                        )}
                    </div>
                )}
                <input value={f.trip} onChange={(e) => setF({ ...f, trip: e.target.value })} data-testid="add-reel-trip"
                    placeholder="Trip / caption (e.g. Manali Snow Special)"
                    className="rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-saffron" />
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="add-reel-name"
                    placeholder="Shown as (default: GhumneChaley Team)"
                    className="rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-saffron" />
                {mode === "upload" && (
                    <div className="sm:col-span-2">
                        <input ref={fileRef} type="file" accept=".mp4,.mov,.webm" className="hidden"
                            onChange={(e) => { setFile(e.target.files?.[0] || null); setProgress(0); }} data-testid="add-reel-file-input" />
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="add-reel-pick-file"
                            className="w-full rounded-xl border-2 border-dashed border-stone-300 bg-white hover:border-saffron py-5 text-sm font-bold text-stone-500 flex flex-col items-center gap-1.5 transition-colors">
                            <UploadCloud className="h-6 w-6 text-saffron" />
                            {file ? `${file.name} (${Math.round(file.size / 1048576)} MB)` : "Tap to pick a video (MP4/MOV/WebM, up to 150MB)"}
                        </button>
                        {busy && (
                            <div className="mt-2 h-2 rounded-full bg-stone-200 overflow-hidden" data-testid="add-reel-progress">
                                <div className="h-full bg-saffron transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        )}
                    </div>
                )}
            </div>
            <button onClick={mode === "link" ? submitLink : submitFile} disabled={busy} data-testid="add-reel-submit"
                className="mt-3 w-full sm:w-auto rounded-full bg-saffron hover:bg-saffron-dark text-white text-xs font-display font-black uppercase tracking-wider px-6 py-2.5 disabled:opacity-60 inline-flex items-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {mode === "upload" ? `Uploading ${progress}%` : "Adding…"}</> : "Publish reel to homepage"}
            </button>
            <p className="mt-2 text-[10px] text-stone-400">Staff reels go live immediately — no moderation step. You can reject or delete them below anytime.</p>
        </div>
    );
}

export function ReelsModeration({ token }) {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("pending");
    const [showAdd, setShowAdd] = useState(false);
    const load = useCallback(async () => {
        try { setData(await fetchAdminReels(token, tab)); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }, [token, tab]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, id, msg) => {
        try { await fn(token, id); toast.success(msg); load(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
    };

    return (
        <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="reels-moderation">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display font-black text-lg text-navy flex items-center gap-2"><Clapperboard className="h-5 w-5 text-saffron" /> Trip Reels</h2>
                    <p className="text-xs text-stone-500 mt-1">Traveller-submitted videos. Approved reels appear on the homepage.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setShowAdd(!showAdd)} data-testid="add-reel-btn"
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-wider transition-colors ${showAdd ? "bg-navy text-white" : "bg-saffron text-white hover:bg-saffron-dark"}`}>
                        {showAdd ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} Add Reel
                    </button>
                    <div className="flex gap-1.5" data-testid="reels-tabs">
                        {["pending", "approved", "rejected"].map((s) => (
                            <button key={s} onClick={() => setTab(s)} data-testid={`reels-tab-${s}`}
                                className={`px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-wider ${tab === s ? "bg-navy text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}>
                                {s}{data?.counts?.[s] != null ? ` (${data.counts[s]})` : ""}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {showAdd && <AddReelPanel token={token} onDone={() => { setShowAdd(false); setTab("approved"); load(); }} />}

            {!data ? (
                <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
            ) : !data.reels.length ? (
                <p className="py-10 text-center text-sm text-stone-400" data-testid="reels-empty">No {tab} reels.</p>
            ) : (
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data.reels.map((r) => (
                        <div key={r.id} className="rounded-2xl border border-stone-200 overflow-hidden" data-testid={`admin-reel-${r.id}`}>
                            <ReelPreview token={token} reel={r} />
                            <div className="p-3">
                                <p className="font-bold text-sm text-navy">{r.trip || "Trip reel"}</p>
                                <p className="text-[11px] text-stone-500">
                                    by {r.person_name} · {r.provider && r.provider !== "upload" ? `${r.provider} link` : `${Math.round(r.size / 1048576)}MB`} · {(r.created_at || "").slice(0, 10)}
                                    {r.source === "staff" && <span className="ml-1 rounded-full bg-navy/10 text-navy px-1.5 py-0.5 text-[9px] font-black uppercase">staff</span>}
                                </p>
                                <div className="mt-2.5 flex gap-1.5">
                                    {r.status !== "approved" && (
                                        <button onClick={() => act((t, id) => moderateReel(t, id, "approve"), r.id, "Reel approved — now live on the homepage")}
                                            data-testid={`reel-approve-${r.id}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-[10px] font-black uppercase tracking-wider py-2">
                                            <CheckCircle2 className="h-3 w-3" /> Approve
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button onClick={() => act((t, id) => moderateReel(t, id, "reject"), r.id, "Reel rejected")}
                                            data-testid={`reel-reject-${r.id}`}
                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-amber-300 text-amber-700 text-[10px] font-black uppercase tracking-wider py-2 hover:bg-amber-50">
                                            <XCircle className="h-3 w-3" /> Reject
                                        </button>
                                    )}
                                    <button onClick={() => window.confirm("Delete this reel permanently?") && act(deleteReel, r.id, "Reel deleted")}
                                        data-testid={`reel-delete-${r.id}`}
                                        className="inline-flex items-center justify-center rounded-full border border-red-200 text-red-600 px-2.5 py-2 hover:bg-red-50">
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
